<template>
  <div class="show_all_content_wrapper">
    <!-- Start:: Main Section -->
    <main>
      <!--  =========== Start:: Filter Form =========== -->
      <div
        class="filter_content_wrapper"
        :class="{ active: filterFormIsActive }"
      >
        <button
          class="filter_toggler"
          @click="filterFormIsActive = !filterFormIsActive"
        >
          <i class="fal fa-times"></i>
        </button>
        <div class="filter_title_wrapper">
          <h5>{{ $t("TITLES.searchBy") }}</h5>
        </div>
        <div class="filter_form_wrapper">
          <form @submit.prevent="submitFilterForm">
            <div class="row justify-content-center align-items-center w-100">
              <!-- Start:: Provider Name Input -->
              <base-input
                col="4"
                type="text"
                :placeholder="$t('PLACEHOLDERS.provider_name')"
                v-model.trim="filterOptions.provider_name"
              />
              <!-- End:: Provider Name Input -->

              <!-- Start:: Request Number Input -->
              <base-input
                col="4"
                type="text"
                :placeholder="$t('WITHDRAW.request_number')"
                v-model.trim="filterOptions.request_number"
              />
              <!-- End:: Request Number Input -->

              <!-- Start:: Provider Type Input -->
              <base-select-input
                col="4"
                :optionsList="providerTypes"
                :placeholder="$t('WITHDRAW.provider_type')"
                v-model="filterOptions.provider_type"
              />
              <!-- End:: Provider Type Input -->
            </div>

            <div class="btns_wrapper">
              <button class="submit_btn" :disabled="isWaitingRequest">
                <i class="fal fa-search"></i>
              </button>
              <button
                class="reset_btn"
                type="button"
                :disabled="isWaitingRequest"
                @click="resetFilter"
              >
                <i class="fal fa-redo"></i>
              </button>
            </div>
          </form>
        </div>
      </div>
      <!--  =========== End:: Filter Form =========== -->

      <!--  =========== Start:: Table Title =========== -->
      <div class="table_title_wrapper">
        <div class="title_text_wrapper">
          <h5>{{ $t("WITHDRAW.withdraw_requests") }}</h5>
          <button
            v-if="!filterFormIsActive"
            class="filter_toggler"
            @click.stop="filterFormIsActive = !filterFormIsActive"
          >
            <i class="fal fa-search"></i>
          </button>
        </div>

        <div class="title_route_wrapper">
          <div class="excel" @click="downloadExcel">
            {{ $t("BUTTONS.downloadExcel") }}
          </div>
        </div>
      </div>
      <!--  =========== End:: Table Title =========== -->

      <!--  =========== Start:: Data Table =========== -->
      <v-data-table
        class="thumb"
        :loading="loading"
        :loading-text="$t('TABLES.loadingData')"
        :search="searchValue"
        :headers="tableHeaders"
        :items="tableRows"
        item-class="ltr"
        :items-per-page="paginations.items_per_page"
        hide-default-footer
      >
        <!-- Start:: Serial Number -->
        <template v-slot:[`item.id`]="{ index }">
          {{ (paginations.current_page - 1) * paginations.items_per_page + index + 1 }}
        </template>
        <!-- End:: Serial Number -->

        <!-- Start:: No Data State -->
        <template v-slot:no-data>
          {{ $t("TABLES.noData") }}
        </template>
        <!-- End:: No Data State -->

        <!-- Start:: Status Badge -->
        <template v-slot:[`item.status_tranlated`]="{ item }">
          <span
            :class="{
              'badge badge-success': item.status === 'accepted',
              'badge badge-warning': item.status === 'new',
              'badge badge-danger': item.status === 'rejected',
            }"
          >
            {{ item.status_tranlated }}
          </span>
        </template>
        <!-- End:: Status Badge -->

        <!-- Start:: Actions -->
        <template v-slot:[`item.actions`]="{ item }">
          <div class="actions">
            <a-tooltip placement="bottom">
              <template slot="title">
                <span>{{ $t("BUTTONS.show") }}</span>
              </template>
              <button class="btn_show" @click="showItem(item)">
                <i class="fal fa-eye"></i>
              </button>
            </a-tooltip>

            <template v-if="item.status === 'new'">
              <a-tooltip placement="bottom">
                <template slot="title">
                  <span>{{ $t("BUTTONS.accept") }}</span>
                </template>
                <button class="btn_activate" @click="acceptItem(item)">
                  <i class="fad fa-check-circle"></i>
                </button>
              </a-tooltip>
              <a-tooltip placement="bottom">
                <template slot="title">
                  <span>{{ $t("BUTTONS.reject") }}</span>
                </template>
                <button
                  class="btn_deactivate"
                  @click="selectRejectItem(item)"
                >
                  <i class="fad fa-times-circle"></i>
                </button>
              </a-tooltip>
            </template>
          </div>
        </template>
        <!-- End:: Actions -->

        <!-- ======================== Start:: Dialogs ======================== -->
        <template v-slot:top>
          <!-- Start:: Reject Modal -->
          <v-dialog v-model="dialogReject">
            <v-card>
              <v-card-title
                class="text-h5 justify-center"
                v-if="itemToReject"
              >
                {{ $t("TITLES.rejectConfirmingMessage", { name: itemToReject.provider_name }) }}
              </v-card-title>

              <form class="w-100">
                <base-input
                  col="12"
                  rows="3"
                  type="textarea"
                  :placeholder="$t('PLACEHOLDERS.reason_reject')"
                  v-model.trim="rejectReason"
                  required
                />
              </form>

              <v-card-actions>
                <v-btn
                  class="modal_confirm_btn"
                  @click="confirmRejectItem"
                  :disabled="!!!rejectReason || rejectReason?.length < 3"
                >
                  {{ $t("BUTTONS.ok") }}
                </v-btn>

                <v-btn
                  class="modal_cancel_btn"
                  @click="dialogReject = false"
                  >{{ $t("BUTTONS.cancel") }}</v-btn
                >
                <v-spacer></v-spacer>
              </v-card-actions>
            </v-card>
          </v-dialog>
          <!-- End:: Reject Modal -->
        </template>
        <!-- ======================== End:: Dialogs ======================== -->
      </v-data-table>
      <!--  =========== End:: Data Table =========== -->
    </main>
    <!-- End:: Main Section -->

    <!-- Start:: Pagination -->
    <template>
      <div class="pagination_container text-center mt-3 mb-0">
        <v-pagination
          class="py-0"
          square
          v-model="paginations.current_page"
          :length="paginations.last_page"
          :total-visible="6"
          @input="updateRouterQueryParam($event)"
          :prev-icon="
            getAppLocale == 'ar' ? 'fal fa-angle-right' : 'fal fa-angle-left'
          "
          :next-icon="
            getAppLocale == 'ar' ? 'fal fa-angle-left' : 'fal fa-angle-right'
          "
        />
      </div>
    </template>
    <!-- End:: Pagination -->
  </div>
</template>

<script>
import { mapGetters } from "vuex";

export default {
  name: "AllWithdrawRequests",

  computed: {
    ...mapGetters({
      getAppLocale: "AppLangModule/getAppLocale",
    }),

    // providerTypes() {
    //   return [
    //     {
    //       id: 1,
    //       name: this.$t("WITHDRAW.cart_owner"),
    //       value: "cart_owner",
    //     },
    //     {
    //       id: 2,
    //       name: this.$t("WITHDRAW.store"),
    //       value: "store",
    //     },
    //     {
    //       id: 3,
    //       name: this.$t("WITHDRAW.photographer"),
    //       value: "photographer",
    //     },
    //     {
    //       id: 4,
    //       name: this.$t("WITHDRAW.barber"),
    //       value: "barber",
    //     },
    //     {
    //       id: 5,
    //       name: this.$t("WITHDRAW.groom"),
    //       value: "groom",
    //     },
    //   ];
    // },
  },

  data() {
    return {
      // Start:: Loading Data
      loading: false,
      isWaitingRequest: false,
      // End:: Loading Data

      // Start:: Filter Data
      filterFormIsActive: false,
      filterOptions: {
        provider_name: null,
        request_number: null,
        provider_type: null,
      },
      // End:: Filter Data
      providerTypes: [],
      // Start:: Table Data
      searchValue: "",
      tableHeaders: [
        {
          text: this.$t("TABLES.Admins.serialNumber"),
          value: "id",
          align: "center",
          sortable: false,
        },
        {
          text: this.$t("WITHDRAW.provider_type"),
          value: "provider_type",
          sortable: false,
          align: "center",
        },
        {
          text: this.$t("PLACEHOLDERS.provider_name"),
          value: "provider_name",
          sortable: false,
          align: "center",
        },
        {
          text: this.$t("WITHDRAW.provider_mobile"),
          value: "provider_mobile",
          sortable: false,
          align: "center",
        },
        {
          text: this.$t("WITHDRAW.request_number"),
          value: "id",
          sortable: false,
          align: "center",
        },
        {
          text: this.$t("WITHDRAW.request_date"),
          value: "created_at",
          sortable: false,
          align: "center",
        },
        {
          text: this.$t("WITHDRAW.request_status"),
          value: "status_tranlated",
          sortable: false,
          align: "center",
        },
        {
          text: this.$t("TABLES.Admins.actions"),
          value: "actions",
          sortable: false,
          align: "center",
        },
      ],
      tableRows: [],
      // End:: Table Data

      // Start:: Dialogs Control Data
      dialogReject: false,
      itemToReject: null,
      rejectReason: "",
      // End:: Dialogs Control Data

      // Start:: Pagination Data
      paginations: {
        current_page: 1,
        last_page: 1,
        items_per_page: 15,
      },
      // End:: Pagination Data
    };
  },

  watch: {
    // Start:: Page Query Param Watcher To Get Page Data Based On It's Change
    ["$route.query.page"]() {
      this.paginations.current_page = +this.$route.query.page;
      this.setTableRows();
    },
    // End:: Page Query Param Watcher To Get Page Data Based On It's Change
  },

  methods: {
    // Start:: Handel Filter
    async submitFilterForm() {
      if (this.$route.query.page !== "1") {
        await this.$router.push({
          path: "/withdraw/all",
          query: { page: 1 },
        });
      }
      this.setTableRows();
    },
    async resetFilter() {
      this.filterOptions.provider_name = null;
      this.filterOptions.request_number = null;
      this.filterOptions.provider_type = null;

      if (this.$route.query.page !== "1") {
        await this.$router.push({
          path: "/withdraw/all",
          query: { page: 1 },
        });
      }
      this.setTableRows();
    },
    // End:: Handel Filter

    // Start:: Set Table Rows
    updateRouterQueryParam(pagenationValue) {
      this.$router.push({
        query: {
          ...this.$route.query,
          page: pagenationValue,
        },
      });

      document.body.scrollTop = 0;
      document.documentElement.scrollTop = 0;
    },

    async setTableRows() {
      this.loading = true;
      try {
        let res = await this.$axios({
          method: "GET",
          url: "withdraw-requests",
          params: {
            page: this.paginations.current_page,
            provider_name: this.filterOptions.provider_name,
            request_number: this.filterOptions.request_number,
            provider_type_id: this.filterOptions.provider_type?.id,
          },
        });
        this.loading = false;

        if (res.data && res.data.data && res.data.data.data) {
          this.excel = res.data.data.export_url;
          this.tableRows = res.data.data.data;
          this.paginations.last_page = res.data.data.meta?.last_page || 1;
          this.paginations.items_per_page = res.data.data.meta?.per_page || 15;
        } else {
          this.tableRows = res.data.data || [];
          this.paginations.last_page = 1;
          this.paginations.items_per_page = 15;
        }
      } catch (error) {
        this.loading = false;
        console.log(error.response?.data?.message || error.message);
      }
    },
    // End:: Set Table Rows

    // Start:: Download Excel
    downloadExcel() {
      if (this.excel) {
        window.open(this.excel, "_blank");
      } else {
        this.$message.error(this.$t("MESSAGES.downloadFailed"));
      }
    },
    // End:: Download Excel

    // ==================== Start:: Crud ====================
    showItem(item) {
      this.$router.push({ path: `/withdraw/show/${item.id}` });
    },

    // Start:: Accept Item
    async acceptItem(item) {
      try {
        await this.$axios({
          method: "POST",
          url: `withdraw-requests/${item.id}/accept`,
        });
        this.$message.success(this.$t("MESSAGES.withdrawAcceptedSuccessfully"));
        this.setTableRows();
      } catch (error) {
        this.$message.error(
          error.response?.data?.message || this.$t("MESSAGES.somethingWrong")
        );
      }
    },
    // End:: Accept Item
    async getProviderTypes(){
      try {
        let res = await this.$axios({
          method: "GET",
          url: `provider-types`,
        });
        console.log(res.data.data.data);
        this.providerTypes = res.data.data.data
      } catch (error) {
        console.log(error.response?.data?.message || error.message);
        return [];
      }
    },
    // Start:: Reject Item
    selectRejectItem(item) {
      this.dialogReject = true;
      this.itemToReject = item;
      this.rejectReason = "";
    },

    async confirmRejectItem() {
      try {
        await this.$axios({
          method: "POST",
          url: `withdraw-requests/${this.itemToReject.id}/reject`,
          data: {
            rejection_reason: this.rejectReason,
          },
        });
        this.dialogReject = false;
        this.$message.success(this.$t("MESSAGES.withdrawRejectedSuccessfully"));
        this.setTableRows();
      } catch (error) {
        this.dialogReject = false;
        this.$message.error(
          error.response?.data?.message || this.$t("MESSAGES.somethingWrong")
        );
      }
    },
    // End:: Reject Item
    // ==================== End:: Crud ====================
  },

  created() {
    // Start:: Fire Methods
    window.addEventListener("click", () => {
      this.filterFormIsActive = false;
    });
    if (this.$route.query.page) {
      this.paginations.current_page = +this.$route.query.page;
    }
    this.setTableRows();
    this.getProviderTypes();
    // End:: Fire Methods
  },
};
</script>

<style scoped>
.badge {
  padding: 6px 12px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 500;
  color: white;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.badge-success {
  background-color: #28a745;
}

.badge-warning {
  background-color: #ffc107;
  color: #212529;
}

.badge-danger {
  background-color: #dc3545;
}

.excel {
  background: darkgreen;
  padding: 7px 10px;
  border-radius: 12px;
  color: #fff;
  cursor: pointer;
}
</style>
